// หน้ารวมเกม: ค่ายเกมของแต่ละหมวด -> รายชื่อเกม -> เข้าเล่น (ข้อมูลจาก API ทั้งหมด)
// URL: /slot /casino /sport /fishing /cards /other /lotto /demo
//      /listgameall/<หมวด>/<ค่าย>   /demo/<ค่าย>
import React, { useEffect, useMemo, useState } from 'react';
import { CATEGORIES, getProviders, getGames, launchGame, launchLotto } from './games';
import { useStore } from './store';
import { t } from './i18n';
import { useLang } from '../site/i18n';
import './memberhub.css';

export const LOBBY_PAGES = ['slot', 'casino', 'sport', 'fishing', 'cards', 'other', 'lotto', 'demo', 'listgameall'];

function parsePath() {
  const seg = window.location.pathname.split('/').filter(Boolean).map(decodeURIComponent);
  if (seg[0] === 'listgameall') return { category: CATEGORIES[seg[1]] ? seg[1] : aliasOf(seg[1]), partner: seg[2] || null };
  if (seg[0] === 'demo') return { category: 'demo', partner: seg[1] || null };
  return { category: CATEGORIES[seg[0]] ? seg[0] : 'slot', partner: null };
}
// ลิงก์แบบ Starbet (/listgameall/gamecard/..., /listgameall/export/...)
function aliasOf(x) {
  return { gamecard: 'cards', export: 'other' }[x] || 'slot';
}

function go(path, setRoute) {
  window.history.pushState(null, '', path);
  setRoute(parsePath());
  window.scrollTo(0, 0);
}

function Loading({ lang }) {
  return <div className="gl-empty"><span className="gl-spinner" /> {t(lang, 'loading')}</div>;
}

export default function GameLobby() {
  const { lang } = useLang();
  const { user } = useStore();
  const [route, setRoute] = useState(parsePath);
  const [providers, setProviders] = useState(null);
  const [games, setGames] = useState(null);
  const [q, setQ] = useState('');
  const cat = CATEGORIES[route.category] || CATEGORIES.slot;
  const isDemo = !!cat.demo;

  useEffect(() => {
    const onPop = () => setRoute(parsePath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // ค่ายเกม
  useEffect(() => {
    let alive = true;
    setProviders(null);
    getProviders(route.category).then((list) => { if (alive) setProviders(list); }).catch(() => alive && setProviders([]));
    return () => { alive = false; };
  }, [route.category, user.username]);

  // เกมของค่าย
  useEffect(() => {
    let alive = true;
    setGames(null); setQ('');
    if (route.partner) getGames(route.category, route.partner).then((l) => { if (alive) setGames(l); }).catch(() => alive && setGames([]));
    return () => { alive = false; };
  }, [route.category, route.partner]);

  const provider = useMemo(() => (providers || []).find((p) => p.partner === route.partner), [providers, route.partner]);
  const shownGames = useMemo(() => {
    let l = games || [];
    if (isDemo) l = l.filter((g) => String(g.demo) === 'true');
    if (q.trim()) l = l.filter((g) => String(g.description || '').toLowerCase().includes(q.trim().toLowerCase()));
    return l;
  }, [games, q, isDemo]);

  const title = t(lang, cat.label);
  const base = isDemo ? '/demo' : '/listgameall/' + route.category;

  // ---------- หวย ----------
  if (route.category === 'lotto') {
    return (
      <section className="gl-wrap">
        <h2 className="gl-titlebar">{title}</h2>
        {!user.loginstate && <div className="gl-note">{t(lang, 'login_first_play')}</div>}
        {providers === null ? <Loading lang={lang} /> : providers.length === 0 ? (
          <div className="gl-empty">{t(lang, 'mh_no_games')}</div>
        ) : (
          <div className="gl-grid">
            {providers.map((it, i) => (
              <button type="button" key={it._id || i} className="gl-card" onClick={() => launchLotto(it.roomid)}>
                {it.image && <img src={it.image} alt={it.name || ''} loading="lazy" />}
                <span className="gl-name">{it.name}</span>
                <span className="gl-play">{t(lang, 'play_btn')}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    );
  }

  // ---------- รายชื่อเกมของค่าย ----------
  if (route.partner) {
    return (
      <section className="gl-wrap">
        <div className="gl-head">
          <button type="button" className="gl-back" onClick={() => go(isDemo ? '/demo' : '/' + route.category, setRoute)}>‹ {t(lang, 'back')}</button>
          <h2 className="gl-title">{provider ? provider.description : route.partner}</h2>
          <input className="gl-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t(lang, 'mh_search_game')} aria-label={t(lang, 'mh_search_game')} />
        </div>
        {isDemo && <div className="gl-note">{t(lang, 'mh_demo_note')}</div>}
        {games === null ? <Loading lang={lang} /> : shownGames.length === 0 ? (
          <div className="gl-empty">{t(lang, 'mh_no_games')}</div>
        ) : (
          <div className="gl-grid gl-grid-games">
            {shownGames.map((g) => (
              <button type="button" key={g.id || g.gameId} className="gl-card gl-game"
                onClick={() => launchGame(g.gameId, g.partner, { demo: isDemo })}>
                <img src={g.image} alt={g.description} loading="lazy" />
                <span className="gl-name">{g.description}</span>
                <span className="gl-play">{isDemo ? t(lang, 'mh_play_demo') : t(lang, 'play_btn')}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    );
  }

  // ---------- ค่ายเกมของหมวด ----------
  return (
    <section className="gl-wrap">
      <h2 className="gl-titlebar">{title}</h2>
      {isDemo && <div className="gl-note">{t(lang, 'mh_demo_note')}</div>}
      {providers === null ? <Loading lang={lang} /> : providers.length === 0 ? (
        <div className="gl-empty">{t(lang, 'mh_no_games')}</div>
      ) : (
        <div className="gl-grid gl-grid-tiles">
          {providers.map((p) => {
            const off = String(p.isMaintenance) === 'true';
            return (
              <button type="button" key={p.id || p.partner} className={'gl-tile' + (off ? ' is-off' : '')} disabled={off}
                title={p.description} aria-label={p.description}
                onClick={() => go(base + '/' + encodeURIComponent(p.partner), setRoute)}>
                <img src={p.image || p.imagesec} alt={p.description} loading="lazy" />
                <span className="gl-tile-over">
                  <span className="gl-play">{off ? t(lang, 'mh_maintenance') : t(lang, 'play_btn')}</span>
                </span>
                {off && <span className="gl-tile-badge">{t(lang, 'mh_maintenance')}</span>}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
